import React, { useState } from 'react';
// @ts-ignore
import styles from './QuestionnaireList.module.scss';
import apiClient from '../../Services/ApiClient';
// @ts-ignore
import moment, { Moment } from 'moment';
import LinkButton from '../Button/LinkButton';
import RemoveButton from '../Button/RemoveButton';
import { Theme } from '../../Enums/Theme.enum';
import QuestionnairePlanningModal from '../../Modules/Questionnaire/Components/QuestionnairePlanning/QuestionnairePlanningModal';
import QuestionnaireResultsModal from '../../Modules/Questionnaire/Components/QuestionnaireResults/QuestionnaireResultsModal';
import useTranslation from '../../Hooks/useTranslation';
import FirstUpper from '../FirstUpper';
import { getMomentDate } from '../../Utils/dates.util';
import TreatmentPatientButton from '../Treatment/TreatmentPatientButton';
import { useAppContext } from '../../Services/AppContext';
import Text from '../Base/Text/Text';

interface BasicUserInfo {
	id: string;
	profile: {
		fullName: string;
	};
}

export interface QuestionnaireSchedule {
	id: string;
	questionnaireId: string;
	questionnaire: {
		id: string;
		title: string;
		code: string;
		type: string;
	};
	frequencyText?: string;
	planedFrom: Date;
	planedTo?: Date;
	title: string;
	byDoctor?: string;
	createdBy?: BasicUserInfo;
	user: BasicUserInfo;
	dateFrom: Date;
	dateTo?: Date;
	repeatHours?: number;
	type?: string;
}

interface Props {
	item: QuestionnaireSchedule;
	key: string;
	patientId: string;
	onUpdate?: () => void;
	showPatientName?: boolean;
}

const QuestionnaireListItem: React.FC<Props> = ({
	item,
	onUpdate,
	patientId,
	showPatientName,
}) => {
	const [detailOpened, setDetailOpened] = useState(false);
	const [isResultOpen, setIsResultOpen] = useState(false);
	const { t } = useTranslation();
	const { workplaceId, userData } = useAppContext();

	const handleShowResults = () => {
		setIsResultOpen(true);
	};

	const handleCancelSending = async () => {
		await apiClient.delete('/questionnaire/schedule/' + item.id);
		onUpdate && onUpdate();
	};

	const handleShowDetail = () => {
		setDetailOpened(true);
	};

	const handleCloseDetail = () => {
		setDetailOpened(false);
		onUpdate && onUpdate();
	};

	const types: any = {
		['periodical']: t('base.regular'),
		['disposable']: t('base.oneTime'),
	};

	let frequencyText: string | undefined = undefined;

	if (item.repeatHours) {
		const repeatDays = item.repeatHours / 24;
		switch (repeatDays) {
			case 1:
				frequencyText = t('component.time.onceADay');
				break;
			case 2:
			case 3:
			case 4:
				frequencyText =
					'1x ' + t('base.for') + ' ' + repeatDays + ' ' + t('base.days.two');
				break;
			default:
				frequencyText =
					'1x ' + t('base.for') + ' ' + repeatDays + ' ' + t('base.days.many');
				break;
		}
	}

	return (
		<div className={styles.item}>
			<div className={styles.content}>
				<div className={styles.a}>
					<div className={styles.title}>{item.title}</div>
					<div className={styles.status}>{item.type && types[item.type]}</div>
				</div>
				<div className={styles.b}>
					{showPatientName && item.user?.profile?.fullName && (
						<div className={styles.info}>
							<span>
								<FirstUpper>{t('base.shared.patient')}</FirstUpper>:
							</span>
							<TreatmentPatientButton
								text={item.user.profile.fullName}
								patientId={patientId}
								workplaceId={workplaceId}
								loggedUserId={userData.id}
								className={styles.patientButton}
							/>
						</div>
					)}
					{item.type === 'periodical' ? (
						<>
							<div className={styles.info}>
								<span>{t('base.scheduledFrom')}:</span>
								{getMomentDate(item.planedFrom).format('DD.MM.yyyy')}
							</div>
							<div className={styles.info}>
								<span>{t('base.scheduleTo')}:</span>
								{item.planedTo ? (
									getMomentDate(item.planedTo).format('DD.MM.yyyy')
								) : (
									<FirstUpper>{t('base.shared.notSpecified')}</FirstUpper>
								)}
							</div>
							<div className={styles.info}>
								<span>{t('base.deliveredOnTime')}:</span>
								{getMomentDate(item.dateFrom).format('HH:mm')}
							</div>
							{frequencyText && (
								<div className={styles.info}>
									<span>{t('base.sendingFrequency')}:</span> {frequencyText}
								</div>
							)}
						</>
					) : (
						<>
							<div className={styles.info}>
								<span>{t('base.scheduledFor')}:</span>
								{getMomentDate(item.planedFrom).format('DD.MM.yyyy HH:mm')}
							</div>
						</>
					)}
					<div className={styles.info}>
						<span>{t('component.questionnaires.fromDoctor')}:</span>
						{item.byDoctor}
					</div>
				</div>
			</div>
			<div className={styles.controls}>
				<LinkButton
					onClick={() => handleShowDetail()}
					title={'base.settings'}
					theme={Theme.BLUE}
					firstUpper
				/>
				<LinkButton
					onClick={() => handleShowResults()}
					title={'base.results'}
					theme={Theme.BLUE}
					firstUpper
				/>
				<RemoveButton
					onClick={() => handleCancelSending()}
					title={'base.cancelSending'}
				/>
			</div>
			<QuestionnairePlanningModal
				opened={detailOpened}
				onClose={handleCloseDetail}
				patientId={patientId}
				schedule={item}
			/>
			<QuestionnaireResultsModal
				isOpen={isResultOpen}
				onClose={() => {
					setIsResultOpen(false);
				}}
				patientId={patientId}
				questionnaireName={item.title}
				questionnaireCode={item.questionnaire.code}
			/>
		</div>
	);
};

export default QuestionnaireListItem;
